/**
 * Pure helpers for locating a config id in the URL and stripping it out.
 *
 * A config id is recognized either as:
 *   - the `?config=` query param (explicit), or
 *   - the first path segment when it starts with the configured prefix
 *     (e.g. `/cfg_abc123`), which disambiguates it from real routes.
 */

export interface ConfigDetection {
  id: string;
  source: 'query' | 'path';
}

export function detectConfigId(
  pathname: string,
  search: string,
  prefix: string,
): ConfigDetection | null {
  const queryId = new URLSearchParams(search).get('config');
  if (queryId) return { id: queryId, source: 'query' };

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (prefix && firstSegment && firstSegment.startsWith(prefix)) {
    return { id: firstSegment, source: 'path' };
  }

  return null;
}

/**
 * Build the cleaned URL with the config id removed.
 * - path source: drop the leading config segment (`/cfg_x/quiz` -> `/quiz`, `/cfg_x` -> `/`)
 * - query source: drop the `config` param
 * Other query params are preserved.
 */
export function buildStrippedUrl(
  pathname: string,
  search: string,
  prefix: string,
): string {
  const params = new URLSearchParams(search);
  params.delete('config');

  let nextPath = pathname;
  const segments = pathname.split('/').filter(Boolean);
  if (prefix && segments[0]?.startsWith(prefix)) {
    nextPath = '/' + segments.slice(1).join('/');
  }
  if (!nextPath.startsWith('/')) nextPath = `/${nextPath}`;

  const qs = params.toString();
  return qs ? `${nextPath}?${qs}` : nextPath;
}
